import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, Search, X } from 'lucide-react';
import api from '@/lib/api';
import type { SupportKbArticleResponse, SupportCategoryAdminResponse } from '@/lib/api';

interface KbFormData {
  title: string;
  content: string;
  categoryId: string;
  keywords: string[];
  isPublished: boolean;
}

const DEFAULT_FORM: KbFormData = {
  title: '',
  content: '',
  categoryId: '',
  keywords: [],
  isPublished: true,
};

export function AdminKnowledgeBasePage() {
  const [articles, setArticles] = useState<SupportKbArticleResponse[]>([]);
  const [categories, setCategories] = useState<SupportCategoryAdminResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState<SupportKbArticleResponse | null>(null);
  const [form, setForm] = useState<KbFormData>(DEFAULT_FORM);
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [kbRes, catRes] = await Promise.all([
        api.listSupportKbArticlesAdmin({
          search: search || undefined,
          limit: 100,
        }),
        api.listSupportCategoriesAdmin(),
      ]);
      setArticles(kbRes.articles);
      setTotal(kbRes.total);
      setCategories(catRes.categories);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingArticle(null);
    setForm(DEFAULT_FORM);
    setKeywordInput('');
    setShowDialog(true);
  };

  const openEdit = (article: SupportKbArticleResponse) => {
    setEditingArticle(article);
    setForm({
      title: article.title,
      content: article.content,
      categoryId: article.categoryId || '',
      keywords: article.keywords || [],
      isPublished: article.isPublished ?? true,
    });
    setKeywordInput('');
    setShowDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingArticle) {
        await api.updateSupportKbArticle(editingArticle.kbId, {
          title: form.title,
          content: form.content,
          categoryId: form.categoryId || null,
          keywords: form.keywords,
          isPublished: form.isPublished,
        });
      } else {
        await api.createSupportKbArticle({
          title: form.title,
          content: form.content,
          categoryId: form.categoryId || undefined,
          keywords: form.keywords,
          isPublished: form.isPublished,
        });
      }
      setShowDialog(false);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (kbId: string) => {
    setDeleting(kbId);
    try {
      await api.deleteSupportKbArticle(kbId);
      fetchData();
    } finally {
      setDeleting(null);
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim().toLowerCase();
    if (kw && !form.keywords.includes(kw)) {
      setForm((f) => ({ ...f, keywords: [...f.keywords, kw] }));
    }
    setKeywordInput('');
  };

  const removeKeyword = (kw: string) => {
    setForm((f) => ({ ...f, keywords: f.keywords.filter((k) => k !== kw) }));
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return '—';
    return categories.find((c) => c.categoryId === categoryId)?.name || '—';
  };

  if (loading && articles.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Knowledge Base Management</h2>
          <p className="text-sm text-muted-foreground">
            {total} article{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Article
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles..."
          className="pl-9"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Category</TableHead>
              <TableHead className="w-56">Keywords</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {search
                    ? 'No articles match your search.'
                    : 'No KB articles yet. Click "Add Article" to create one.'}
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.kbId}>
                  <TableCell>
                    <div className="font-medium">{article.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-md">
                      {article.content.substring(0, 120)}...
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getCategoryName(article.categoryId)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(article.keywords || []).slice(0, 4).map((kw) => (
                        <Badge key={kw} variant="secondary" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                      {(article.keywords || []).length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{article.keywords.length - 4}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={article.isPublished ? 'default' : 'secondary'}>
                      {article.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(article)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={deleting === article.kbId}
                        onClick={() => handleDelete(article.kbId)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Edit Article' : 'Add Article'}</DialogTitle>
            <DialogDescription>
              {editingArticle
                ? 'Update the knowledge base article.'
                : 'Create a new knowledge base article for AI-assisted support.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Article title"
                  maxLength={500}
                />
              </div>

              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="Write the article content (supports markdown)..."
                  rows={12}
                  maxLength={50000}
                />
                <p className="text-xs text-muted-foreground">
                  {form.content.length.toLocaleString()} / 50,000 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category (optional)</Label>
                  <Select
                    value={form.categoryId || 'none'}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, categoryId: v === 'none' ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categories
                        .filter((c) => c.isActive)
                        .map((c) => (
                          <SelectItem key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-3 pt-7">
                  <Switch
                    checked={form.isPublished}
                    onCheckedChange={(checked) => setForm((f) => ({ ...f, isPublished: checked }))}
                  />
                  <Label>Published</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Keywords</Label>
                <div className="flex gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder="Type keyword and press Enter"
                  />
                  <Button variant="outline" onClick={addKeyword} disabled={!keywordInput.trim()}>
                    Add
                  </Button>
                </div>
                {form.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.keywords.map((kw) => (
                      <Badge key={kw} variant="secondary" className="gap-1 pr-1">
                        {kw}
                        <button
                          onClick={() => removeKeyword(kw)}
                          className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.title.trim() || !form.content.trim()}
            >
              {saving ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
